import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { OWNER_LABEL, PASSWORD_MIN_LENGTH, ROLE_LABELS } from '@bingo/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ThemeSwitch } from '../components/ThemeSwitch';
import { LangSwitch } from '../components/LangSwitch';
import { useT } from '../lib/i18n';
import { OwnerPanel } from '../components/OwnerPanel';
import { PasswordInput } from '../components/PasswordInput';

export function Account() {
  const { me, logout } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [msg, setMsg] = useState<{ kind: 'good' | 'bad'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const t = useT();
  if (!me) return null;

  async function changePassword(e: FormEvent) {
    e.preventDefault();
    setMsg(null);
    if (next.length < PASSWORD_MIN_LENGTH) return setMsg({ kind: 'bad', text: t('Use at least {n} characters.', { n: PASSWORD_MIN_LENGTH }) });
    setBusy(true);
    try {
      await api('/api/me/password', { method: 'POST', body: { currentPassword: current, newPassword: next } });
      setCurrent('');
      setNext('');
      setMsg({ kind: 'good', text: t('Password changed. Your other devices were logged out.') });
    } catch (err) {
      setMsg({ kind: 'bad', text: err instanceof ApiError ? err.message : t('Could not change the password.') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="stack" style={{ maxWidth: 560 }}>
      <div className="page-head"><h1>{t('My account')}</h1></div>
      <div className="card section">
        <h2>{me.user.name}</h2>
        <div className="muted">{me.user.email} · {t(me.owner ? OWNER_LABEL : ROLE_LABELS[me.user.role])}</div>
        {me.user.role === 'direction' && <div><Link className="btn" to="/users">{t('Manage users')}</Link></div>}
      </div>
      {me.owner && <OwnerPanel status={me.owner} />}
      <div className="card section">
        <h2>{t('Language')}</h2>
        <div><LangSwitch /></div>
        <span className="muted" style={{ fontSize: 13 }}>{t('Only the screens change language. Tour names, hotels and notes stay as they are.')}</span>
      </div>
      <div className="card section">
        <h2>{t('Theme')}</h2>
        <div><ThemeSwitch /></div>
        <span className="muted" style={{ fontSize: 13 }}>{t('Auto follows the day or night mode of your phone or computer.')}</span>
      </div>
      <form className="card section" onSubmit={changePassword}>
        <h2>{t('Change password')}</h2>
        {msg && <div className={`alert alert-${msg.kind}`}>{msg.text}</div>}
        <div className="field">
          <label htmlFor="cur">{t('Current password')}</label>
          <PasswordInput id="cur" className="input" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="new">{t('New password')}</label>
          <PasswordInput id="new" className="input" autoComplete="new-password" required value={next} onChange={(e) => setNext(e.target.value)} />
        </div>
        <div><button className="btn btn-primary" disabled={busy}>{t('Change password')}</button></div>
      </form>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn" onClick={logout}>{t('Log out')}</button>
        <span className="version">Bingo App v {me.version}</span>
      </div>
    </div>
  );
}
